import React from 'react';
import { View } from 'react-native';
import { Button, Card, Chip, Icon, Text, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';

export interface ConnectReadiness {
  state: string; payouts_enabled: boolean; charges_enabled: boolean;
  requirements_due_count: number; provider_mode: string;
}
// Created/not-started use the setup CTA; the rest resume an existing setup.
const SETUP_STATES = new Set(['not_started', 'created']);
const RESUME_STATES = new Set(['action_required', 'verification_pending', 'restricted']);
// The Stripe Express dashboard exists once the partner has submitted the Stripe form.
const DASHBOARD_STATES = new Set(['enabled', 'verification_pending', 'action_required', 'restricted']);
type StepState = 'done' | 'current' | 'todo' | 'alert';

function steps(state: string): StepState[] {
  if (state === 'enabled') return ['done', 'done', 'done'];
  if (state === 'verification_pending') return ['done', 'current', 'todo'];
  if (state === 'action_required' || state === 'restricted') return ['alert', 'todo', 'todo'];
  return ['current', 'todo', 'todo'];
}
const ICONS: Record<StepState, string> = { done: 'check-circle', current: 'arrow-right-circle', todo: 'circle-outline', alert: 'alert-circle' };

interface Props {
  connect: ConnectReadiness | null; unavailable: boolean; opening: boolean; refreshing: boolean; busy: boolean;
  fallbackReady: boolean; onStart: () => void; onRefresh: () => void; onFallback: () => void;
  dashboardOpening?: boolean; onDashboard?: () => void;
}

/** Interactive Stripe-hosted onboarding: state comes from the backend readback, never from a returned URL. */
export function ConnectCard({ connect, unavailable, opening, refreshing, busy, fallbackReady, onStart, onRefresh, onFallback, dashboardOpening = false, onDashboard }: Props) {
  const { t } = useTranslation('partner');
  const theme = useTheme();
  const live = !unavailable && connect ? connect : null;
  const cta = live && SETUP_STATES.has(live.state) ? { label: t('connectStart'), testID: 'connect-start' }
    : live && RESUME_STATES.has(live.state) ? { label: t('connectContinue'), testID: 'connect-continue' } : null;
  const dashboard = !!onDashboard && !!live && DASHBOARD_STATES.has(live.state);
  const touch = { minHeight: 44 };
  return <Card testID="connect-card" style={{ borderRadius: 16 }}><Card.Content style={{ gap: 12, paddingVertical: 12 }}>
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
      <Text variant="titleLarge" style={{ flexShrink: 1 }}>{t('connectTitle')}</Text>
      {live?.provider_mode === 'test' && <Chip compact icon="flask-outline">{t('connectTestMode')}</Chip>}
    </View>
    <Text>{t(live ? `connectState_${live.state}` : 'connectUnavailable')}</Text>
    {live && live.requirements_due_count > 0 && live.state !== 'enabled' && <Text variant="labelLarge">{t('connectRequirements', { count: live.requirements_due_count })}</Text>}
    {live && <View style={{ gap: 8 }}>
      {steps(live.state).map((step, index) => <View key={index} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Icon source={ICONS[step]} size={20} color={step === 'alert' ? theme.colors.error : step === 'todo' ? theme.colors.outline : theme.colors.primary} />
        <Text style={{ flexShrink: 1, fontWeight: step === 'current' || step === 'alert' ? '600' : '400' }}>{t(`connectStep${index + 1}`)}</Text>
      </View>)}
    </View>}
    {cta && <Button mode="contained" icon="open-in-new" testID={cta.testID} contentStyle={touch} loading={opening} disabled={busy || opening}
      accessibilityLabel={cta.label} onPress={onStart}>{cta.label}</Button>}
    {dashboard && <View style={{ gap: 4 }}>
      <Button mode="outlined" icon="open-in-new" testID="connect-dashboard" contentStyle={touch} loading={dashboardOpening}
        disabled={busy || opening || dashboardOpening} accessibilityLabel={t('connectDashboard')} onPress={onDashboard}>{t('connectDashboard')}</Button>
      <Text testID="connect-dashboard-help" variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('connectDashboardHelp')}</Text>
    </View>}
    {fallbackReady && <Button mode="contained-tonal" icon="open-in-new" testID="connect-open-fallback" contentStyle={touch} onPress={onFallback}>{t('connectOpenFallback')}</Button>}
    <Button mode="outlined" icon="refresh" testID="connect-refresh" contentStyle={touch} loading={refreshing} disabled={refreshing || opening || dashboardOpening} onPress={onRefresh}>{t('connectRefresh')}</Button>
    <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('connectKeepSharing')}</Text>
  </Card.Content></Card>;
}
